import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { gatewayClient, setToken, leaveToDashboard } from "@/lib/api";
import { clearAccountCaches } from "@/lib/pwa";
import { clearInstallDismissal } from "@/hooks/useInstallPrompt";

const AuthContext = createContext(null);

// A copy of the signed-in profile, kept on this device so the app can still
// open offline. It is removed on sign-out. It is never used to authorise
// anything: the server still checks every request.
const USER_KEY = "hv.user";
function saveUser(user) {
  try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch { /* ignore */ }
  return user;
}
function readSavedUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, object = authed
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(saveUser(data.user));
    } catch (err) {
      // No response at all means no connection, not a rejected session. Keep
      // the family in the app with the saved profile, so downloaded stories
      // still open. A real rejection (expired token, bad login) still signs out.
      const saved = readSavedUser();
      if (!err?.response && saved && localStorage.getItem("hv_token")) {
        setUser(saved);
      } else {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Signing out of the Super App ends every app's session. When this tab comes back into view, ask the gateway once whether the session is
  // still alive: a rejected token (401) means the person signed out elsewhere, so this app shows the signed-out screen too.
  useEffect(() => {
    let last = 0;
    const recheck = () => {
      if (document.visibilityState === "hidden" || !localStorage.getItem("hv_token") || Date.now() - last < 10000) return;
      last = Date.now();
      client.get("/auth/me").catch((err) => {
        if (err?.response?.status === 401) {
          try { localStorage.removeItem("hv_token"); localStorage.removeItem(USER_KEY); } catch { /* ignore */ }
          setUser(false);
        }
      });
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => { window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); };
  }, []);

  // Authenticate against hikmah-backend (the gateway), not this app's own
  // /auth/login. The gateway only returns {token, user_id}, so hydrate the
  // Stories-shaped profile from the proxied /auth/me right after.
  const login = async (email, password) => {
    const { data } = await gatewayClient.post("/auth/login", { email, password });
    if (data.password_reset_required) return data; // { password_reset_required, reset_token }
    setToken(data.token);
    const me = await client.get("/auth/me");
    setUser(me.data.user);
    return me.data.user;
  };
  // Completes the gateway's forced-password-update step (accounts merged or
  // migrated from another system), then logs the user in for real.
  const forcePasswordUpdate = async (resetToken, newPassword) => {
    const { data } = await gatewayClient.post("/auth/force-password-update", {
      reset_token: resetToken, new_password: newPassword,
    });
    setToken(data.token);
    const me = await client.get("/auth/me");
    setUser(me.data.user);
    return me.data.user;
  };
  const register = async (payload) => {
    // The gateway account only takes email/password/name; the first proxied
    // call JIT-creates the bare Stories profile, then the extra signup fields
    // are saved onto it. (`role` isn't carried — gateway signups start as
    // students; parent/teacher roles are assigned via admin.)
    const { data } = await gatewayClient.post("/auth/register", {
      email: payload.email, password: payload.password, name: payload.name,
    });
    setToken(data.token);
    await client.get("/auth/me");
    const { data: prof } = await client.patch("/me/profile", {
      age: payload.age ?? undefined, language: payload.language,
      parent_contact: payload.parent_contact || undefined,
    });
    setUser(prof.user);
    return prof.user;
  };
  const logout = async () => {
    try { await client.post("/auth/logout"); } catch (e) { /* ignore */ }
    try { localStorage.removeItem(USER_KEY); } catch { /* ignore */ }
    // Downloaded stories and cached media belong to this account, so nothing
    // is left for the next person on this device. A "Not now" install choice
    // also goes, since it was theirs.
    await clearAccountCaches();
    clearInstallDismissal();
    leaveToDashboard();
    setUser(false);
  };
  const refresh = async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(saveUser(data.user));
    } catch (err) {
      const saved = readSavedUser();
      setUser(!err?.response && saved ? saved : false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, forcePasswordUpdate, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
